import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";

import Button from "components/library/Button";
import TextInput from "components/library/TextInput";
import Popover from "components/library/Popover";
import shared from "../../../shared.css";
import styles from "./LinkEditor.css";

import InputError from "editor/utils/InputError";
import activeEntityKey from "editor/utils/activeEntityKey";
import isActiveEntity from "editor/utils/isActiveEntity";
import getActiveEditorState from "editor/utils/getActiveEditorState";
import getActiveEntityText from "editor/utils/getActiveEntityText";
import getSelectedBlockElement from "editor/utils/getSelectedBlockElement";
import getSelectionText from "editor/utils/getSelectionText";
import { EDITOR_MODALS, ENTITY_TYPE } from "config/constants/editor";
import { URL_REGEX } from "config/constants";
import { getVisibleSelectionRect } from "draft-js";
import { toggleLink } from "editor/actions/editor";
import { toggleModal } from "editor/actions/ui";

class LinkEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      linkText:
        props.entityKey === activeEntityKey(props.editorState)
          ? getActiveEntityText(props.editorState)
          : getSelectionText(props.editorState),
      url: this.props.url,
      urlError: null,
    };

    this.closeModal = this._closeModal.bind(this);
    this.handleOnClick = this._handleOnClick.bind(this);
    this.handleOnChange = this._handleOnChange.bind(this);
    this.handleURLChange = this._handleURLChange.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      linkText:
        nextProps.entityKey === activeEntityKey(nextProps.editorState)
          ? getActiveEntityText(nextProps.editorState)
          : getSelectionText(nextProps.editorState),
      url: nextProps.url,
    });
  }

  _handleOnClick(e) {
    e.preventDefault();
    this.props.toggleLink(this.state.url, this.state.linkText);
    this.props.toggleModal(EDITOR_MODALS.CLOSE);
  }

  _handleURLChange(e) {
    e.target.value = e.target.value.trim();
    this.handleOnChange(e);
  }

  _handleOnChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  _closeModal() {
    this.props.toggleModal(EDITOR_MODALS.CLOSE);
    this.setState({ urlError: null });
  }

  isOpened() {
    if (this.props.openModal !== EDITOR_MODALS.LINK_EDITOR) return false;

    const linkActive =
      isActiveEntity(this.props.editorState, ENTITY_TYPE.LINK) ||
      isActiveEntity(this.props.editorState, ENTITY_TYPE.OLD_LINK);

    return this.props.entityKey
      ? linkActive &&
          this.props.entityKey === activeEntityKey(this.props.editorState)
      : !linkActive;
  }

  calculateOffset() {
    // TODO: DRY; derived from EmbedEditor

    if (this.props.openModal !== EDITOR_MODALS.LINK_EDITOR) {
      return null;
    }

    let anchor;

    if (!this.props.linkEl) {
      const selectionRect = getVisibleSelectionRect(window);

      // anchor to cursor
      if (selectionRect) {
        anchor = selectionRect;

        // anchor to an empty content block (paragraph)
      } else {
        const blockElement = getSelectedBlockElement();

        if (blockElement) anchor = blockElement.getBoundingClientRect();
        else return null;
      }
    } else {
      // anchor to existing link
      anchor = this.props.linkEl.getBoundingClientRect();
    }

    let { top, left } = anchor;
    const editorElement = document.querySelector(".link-editor");
    const editorHeight = editorElement ? editorElement.clientHeight : 279;
    const spacing = parseFloat(shared["scale-3"]) * 10;
    const BUFFER = spacing;

    top -= editorHeight + spacing;

    // check top bound and adjust if necessary
    if (top < BUFFER) {
      top = BUFFER;
    }

    return { top, left };
  }

  render() {
    return (
      <Popover
        className={classNames("link-editor", styles.root)}
        closeModal={this.closeModal}
        isOpened={this.isOpened()}
        position={this.calculateOffset()}
      >
        <form>
          <TextInput
            className="gtm-link-field"
            label="Link text"
            name="linkText"
            onChange={this.handleOnChange}
            placeholder="Type something"
            type="text"
            value={this.state.linkText}
          />
          <TextInput
            autoFocus
            error={
              this.state.urlError &&
              new InputError(this.state.urlError, null, null, "Incorrect URL")
            }
            label="URL"
            name="url"
            onBlur={e => {
              this.setState({
                urlError: URL_REGEX.test(e.target.value)
                  ? null
                  : "Please insert a valid URL",
              });
            }}
            onChange={this.handleURLChange}
            placeholder="Type a URL"
            type="text"
            value={this.state.url}
          />
          <Button
            className="gtm-apply-link"
            fullWidth
            isDisabled={
              !this.state.linkText || !this.state.url || !!this.state.urlError
            }
            onClick={this.handleOnClick}
            type="primary"
          >
            Apply
          </Button>
        </form>
      </Popover>
    );
  }
}

const mapStateToProps = state => {
  return {
    openModal: state.editor.ui.openModal,
    editorState: getActiveEditorState(state),
  };
};

const mapDispatchToProps = {
  toggleLink,
  toggleModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LinkEditor);

LinkEditor.defaultProps = {
  url: "",
};

LinkEditor.propTypes = {
  url: PropTypes.string,
  toggleLink: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  openModal: PropTypes.oneOf(Object.values(EDITOR_MODALS)),
  editorState: PropTypes.object.isRequired,
  entityKey: PropTypes.string,
  linkEl: PropTypes.instanceOf(Element),
};
